import React, { useState } from 'react';
import { format, isSameMonth, isSameDay, startOfMonth, getDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { FiCalendar, FiCheck, FiX, FiExternalLink } from 'react-icons/fi';
import { markAppointmentAttendance, saveAttendanceLocally } from '../../services/appointmentService';
import './CalendarGrid.css';

const WEEKDAYS = ['DOM', 'LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB'];

const CalendarGrid = ({ days, appointments, currentDate, onAppointmentUpdate }) => {
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [loading, setLoading] = useState(false);
  
  // Obtener el día de la semana del primer día del mes (0 = domingo, 1 = lunes, etc.)
  const firstDayOfMonth = startOfMonth(currentDate);
  const startingDayIndex = getDay(firstDayOfMonth);
  
  // Crear array con días vacíos para completar la primera semana
  const blanks = Array(startingDayIndex).fill(null);
  
  const handleAppointmentClick = (appointment, event) => {
    // Detener la propagación para evitar que se cierre inmediatamente
    event.stopPropagation();
    
    // Calcular posición para el modal
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    setModalPosition({
      top: rect.top + scrollTop + rect.height + 5,
      left: rect.left
    });
    
    setSelectedAppointment(appointment);
  };
  
  const closeModal = () => {
    setSelectedAppointment(null);
  };
  
  const handleAttendanceChange = async (attended) => {
    if (!selectedAppointment || loading) return;
    
    try {
      setLoading(true);
      
      // Llamar al servicio para marcar asistencia
      const result = await markAppointmentAttendance(selectedAppointment.id, attended);
      
      // Para desarrollo, guardar localmente
      saveAttendanceLocally(selectedAppointment.id, attended);
      
      // Actualizar el estado local y global
      const updatedAppointment = { ...selectedAppointment, attended };
      
      // Si hay una función de callback para actualización, la llamamos
      if (onAppointmentUpdate) {
        onAppointmentUpdate(updatedAppointment);
      }
      
      // Cerrar el modal después de un breve retraso para feedback visual
      setTimeout(() => {
        setSelectedAppointment(updatedAppointment);
        setTimeout(() => {
          closeModal();
          setLoading(false);
        }, 500);
      }, 300);
      
    } catch (error) {
      console.error('Error al marcar asistencia:', error);
      setLoading(false);
    }
  };
  
  // Función para determinar la clase CSS basada en el tipo de cita
  const getAppointmentClass = (appointmentType) => {
    const type = appointmentType.toLowerCase();
    if (type.includes('funcional')) return 'appointment-funcional';
    if (type.includes('musc')) return 'appointment-musculacion';
    if (type.includes('cardio')) return 'appointment-cardio';
    if (type.includes('eval')) return 'appointment-evaluacion';
    return '';
  };
  
  // Cerrar el modal al hacer clic fuera de él
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedAppointment && !event.target.closest('.appointment-modal')) {
        closeModal();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedAppointment]);
  
  return (
    <div className="calendar-grid-container">
      <div className="calendar-header">
        {WEEKDAYS.map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
      </div>
      
      <div className="calendar-grid">
        {/* Días vacíos para completar la primera semana */}
        {blanks.map((_, index) => (
          <div key={`blank-${index}`} className="calendar-day empty"></div>
        ))}
        
        {/* Días del mes actual */}
        {days.map(day => {
          // Filtrar citas para este día
          const dayAppointments = appointments.filter(app => 
            isSameDay(parseISO(app.date), day)
          );
          
          return (
            <div 
              key={day.toString()} 
              className={`calendar-day ${!isSameMonth(day, currentDate) ? 'other-month' : ''} ${isSameDay(day, new Date()) ? 'today' : ''}`}
            >
              <div className="day-number">{format(day, 'd')}</div>
              
              {dayAppointments.length > 0 && (
                <div className="day-appointments">
                  {dayAppointments.slice(0, 3).map(app => (
                    <div 
                      key={app.id} 
                      className={`day-appointment ${getAppointmentClass(app.type)} ${app.attended === true ? 'attended' : app.attended === false ? 'not-attended' : ''}`}
                      onClick={(e) => handleAppointmentClick(app, e)}
                    >
                      <span className="appointment-title">{app.title}</span>
                      {app.attended === true && <span className="attendance-indicator attended"></span>}
                      {app.attended === false && <span className="attendance-indicator not-attended"></span>}
                    </div>
                  ))}
                  
                  {dayAppointments.length > 3 && (
                    <div className="more-appointments">
                      +{dayAppointments.length - 3} más
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Modal de cita seleccionada */}
      {selectedAppointment && (
        <div 
          className="appointment-modal"
          style={{ 
            top: `${modalPosition.top}px`, 
            left: `${modalPosition.left}px` 
          }}
        >
          <div className="appointment-modal-arrow"></div>
          <div className="appointment-modal-content">
            <div className={`appointment-modal-header ${getAppointmentClass(selectedAppointment.type)}`}>
              <FiCalendar className="appointment-modal-icon" />
              <h3 className="appointment-modal-title">{selectedAppointment.title}</h3>
            </div>
            
            <div className="appointment-modal-body">
              <p className="appointment-client">
                <strong>Cliente:</strong> {selectedAppointment.clientName}
              </p>
              
              <div className="appointment-actions">
                <button 
                  className={`attendance-button attended ${selectedAppointment.attended === true ? 'active' : ''} ${loading ? 'disabled' : ''}`}
                  onClick={() => handleAttendanceChange(true)}
                  disabled={loading}
                >
                  <FiCheck className="attendance-icon" />
                  <span>Asistió</span>
                </button>
                
                <button 
                  className={`attendance-button not-attended ${selectedAppointment.attended === false ? 'active' : ''} ${loading ? 'disabled' : ''}`}
                  onClick={() => handleAttendanceChange(false)}
                  disabled={loading}
                >
                  <FiX className="attendance-icon" />
                  <span>No asistió</span>
                </button>
              </div>
              
              <a 
                href={selectedAppointment.calendarUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="calendar-link"
              >
                <FiExternalLink className="calendar-link-icon" />
                <span>Ver en Calendar</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarGrid;